// Общий форматтер эффектов (для окошка описания навыка SkillTooltip и, в перспективе, панели эффектов).
// Превращает {stat, mode, value} (форма unit_effects сервера) в строку для показа: цветная иконка
// параметра + короткая метка + значение (+/− и % где нужно) + тон (бафф/дебафф по знаку).
// ЦВЕТ = СТИХИЯ (красный=физ, синий=маг), ФОРМА иконки = тип параметра — единый язык с InfoPanels.

// доли, которые показываем в процентах (уклонение/резисты)
import { t } from '@/i18n';

export const PERCENT_FIELDS = new Set(['dodge', 'physical_resist', 'magic_resist']);

export const ICON_PATHS = {
  sword: 'M12 2 L14.6 9 L13 15 L11 15 L9.4 9 Z M7.5 13.5 H16.5 V16 H7.5 Z M11 16 H13 V20.5 H11 Z',
  shield: 'M4 4 H20 V12 L12 21 L4 12 Z',
  bolt: 'M13 2 L4 13.5 H10 L8.5 22 L20 9 H13 Z',
  heart: 'M12 20.5 C12 20.5 3.5 14 3.5 8.3 C3.5 5.7 5.6 4 7.8 4 C9.6 4 11.2 5.2 12 6.6 C12.8 5.2 14.4 4 16.2 4 C18.4 4 20.5 5.7 20.5 8.3 C20.5 14 12 20.5 12 20.5 Z',
  drop: 'M12 3 C13 8 18 12 18 15 A6 6 0 1 1 6 15 C6 12 11 8 12 3 Z',
  eye: 'M2 12 C5 7 9 6 12 6 C15 6 19 7 22 12 C19 17 15 18 12 18 C9 18 5 17 2 12 Z',
  arrow: 'M12 8.5 L15.6 13 H13.4 V17.5 H10.6 V13 H8.4 Z', // стрелка вверх (на каплю/сердце = РЕГЕН)
};

// группа стата → {иконка, цвет, regen?}. ЦВЕТ = стихия, ФОРМА = тип. regen → иконка ресурса + стрелка.
export const GROUP_META = {
  pdmg: { icon: 'sword', color: '#e03131' },
  mdmg: { icon: 'sword', color: '#1c7ed6' },
  pres: { icon: 'shield', color: '#e03131' },
  mres: { icon: 'shield', color: '#1c7ed6' },
  speed: { icon: 'bolt', color: '#f08c00' },
  dodge: { icon: 'eye', color: '#9c36b5' },
  health: { icon: 'heart', color: '#e03131' },
  hreg: { icon: 'heart', color: '#e03131', regen: true },
  mana: { icon: 'drop', color: '#1c7ed6' },
  mreg: { icon: 'drop', color: '#1c7ed6', regen: true },
  stamina: { icon: 'drop', color: '#2f9e44' },
  sreg: { icon: 'drop', color: '#2f9e44', regen: true },
};

// Короткая локализованная метка параметра (для окошка навыка и карточек предметов); max_* — отдельной
// строкой возле ресурса. Неизвестный ключ отдаём как есть (новый стат с сервера не ломает интерфейс).
export function statShort(stat) {
  const key = `stat.${stat}`;
  const label = t(key);
  return label === key ? stat : label;
}

// порядок показа эффектов в строках
export const STAT_ORDER = [
  'physical_damage', 'magic_damage', 'physical_resist', 'magic_resist', 'speed', 'dodge',
  'health', 'max_health', 'health_regeneration',
  'mana', 'max_mana', 'mana_regeneration',
  'stamina', 'max_stamina', 'stamina_regeneration',
];

export function statGroup(stat) {
  switch (stat) {
    case 'physical_damage': return 'pdmg';
    case 'magic_damage': return 'mdmg';
    case 'physical_resist': return 'pres';
    case 'magic_resist': return 'mres';
    case 'speed': return 'speed';
    case 'dodge': return 'dodge';
    case 'health_regeneration': return 'hreg';
    case 'mana_regeneration': return 'mreg';
    case 'stamina_regeneration': return 'sreg';
    case 'mana': case 'max_mana': return 'mana';
    case 'stamina': case 'max_stamina': return 'stamina';
    default: return 'health'; // health / max_health
  }
}

// {stat,mode,value} → { n, pct }: mult→±% (множитель−1), доли(резист/уклон)→±%, плоское→±N
// 🔴 `mode: 'pct'` — ЗНАЧЕНИЕ ПРИВЯЗАНО К СТАТУ ФИГУРЫ (решение оунера 2026-09-12): `value` это
// процент от стата `src`, а `abs` (если сервер знал фигуру) — уже посчитанное число. Показываем
// ЧИСЛО, когда оно известно: игроку важно «+420», а не «+25%»; сам процент уходит в подпись-пояснение.
export function effectNum(s) {
  if (s.mode === 'pct') {
    if (s.abs == null) return { n: s.value, pct: true };
    return PERCENT_FIELDS.has(s.stat) ? { n: Math.round(s.abs * 100), pct: true } : { n: s.abs, pct: false };
  }
  if (s.mode === 'mult') return { n: Math.round((s.value - 1) * 100), pct: true };
  if (PERCENT_FIELDS.has(s.stat)) return { n: Math.round(s.value * 100), pct: true };
  return { n: s.value, pct: false };
}

export function statIcon(stat) {
  const meta = GROUP_META[statGroup(stat)];
  if (!meta) return null;
  return { d: ICON_PATHS[meta.icon], color: meta.color, d2: meta.regen ? ICON_PATHS.arrow : null };
}

// {stat,mode,value} → строка для рендера: { icon, label, value:'+20%', tone:'buff'|'debuff' }
// 🔴🔴 СТРОКА-ПОЯСНЕНИЕ ПОД ЭФФЕКТОМ ОТМЕНЕНА И НЕ ВОСКРЕШАТЬ (решение оунера 2026-09-12: «надо
// убрать вообще все новые приписки в описании активных навыков, которые ты сегодня добавил (в игре,
// не в админке)»). Привязка значения к стату — настройка АДМИНКИ, и объясняет себя она там; окошко
// навыка печатает только САМО ЧИСЛО, как до привязок: «Маг. урон −10%». Прежние правила «пояснение
// обязательно» и «пояснение называет владельца» (`game.pctOfStat`, `game.pctOfStatTarget`) сняты
// одним и тем же решением, ключи удалены из обеих локалей.
// 🔴 Что ОСТАЛОСЬ от привязок в показе — только разбор `mode: 'pct'` в `effectNum`: это не приписка,
// а само значение (посчитанное число при известной фигуре, иначе процент).
export function formatEffect(s) {
  const { n, pct } = effectNum(s);
  const num = Math.round(n * 10) / 10;
  return {
    icon: statIcon(s.stat),
    label: statShort(s.stat),
    value: `${num >= 0 ? '+' : ''}${num}${pct ? '%' : ''}`,
    tone: num > 0 ? 'buff' : (num < 0 ? 'debuff' : null),
  };
}

// список эффектов → отсортированные отформатированные строки
export function formatEffects(list) {
  return (list || [])
    .slice()
    .sort((a, b) => STAT_ORDER.indexOf(a.stat) - STAT_ORDER.indexOf(b.stat))
    .map(formatEffect);
}
